import { FC, useCallback, useEffect } from 'react';
import { ArrowLeftIcon } from 'Components';
import { Box, Button, Typography, IconButton } from '@material-ui/core';

import './MobilePaymentOrder.scss';
import { gutters } from 'Theme/Variables';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useLanguage, useLocalization, useQuery, useMobileMasterpassVersion } from 'Hooks';
import { onGetMasterpassForMobileRequest } from 'Stores/CreditCardTransactions';
import { MasterPassTokenEnum, generateLanguageParam } from 'Utils';
import { masterPassRecurringPaymentNormalize } from 'Normalize/MasterPassRequestNormalize';
import { routePath } from 'Navigator/routes';
import { onInıtDataRequest, setLanguage } from 'Stores/App';

type MobilePaymentOrderProps = {};

const MobilePaymentOrder: FC<MobilePaymentOrderProps> = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const url = useQuery();
  const i18n = useLanguage();

  const {
    web_automatic_instruction_edit_page_title,
    web_automatic_instruction_apply_instruction,
    // v2'de talimat SİLME de bu sayfadan geçiyor; ekleme metinleriyle açılmamalı.
    web_title_delete_instruction_popup,
    web_btn_delete_instruction,
    web_automatic_instruction_card_to_be_loaded,
    web_automatic_instruction_payment_instrument,
    web_automatic_instruction_lower_limit,
    web_automatic_instruction_loading_amount,
    web_automatic_instruction_edit_dipnot,
    web_automatic_instruction_edit_apply_instruction,
    web_automatic_instruction_edit_giveup_instruction,
    web_instruction_transaction_fee,
  } = useLocalization();

  const cardName = url.get('cardName');
  // ⚠️ Native'in ilettiği Masterpass sürüm bayrağı — sayfa açılışında bir kez
  // store'a yazılır; sonraki tüm dispatch'ler (OTP dahil) oradan okur.
  useMobileMasterpassVersion(url.get('isVersion2Active'));
  const cardNo = url.get('cardNo');
  const nickName = url.get('nickName');
  const aliasNumber = url.get('aliasNumber');
  const lowerCredit = url.get('lowerCredit');
  const endDate = url.get('endDate');
  const phoneNumber = url.get('phoneNumber');
  const credit = url.get('credit');
  const amount = url.get('amount');
  const language = url.get('language');
  const actionType = url.get('actionType');
  const productId = url.get('productId');
  const token = url.get('token');
  const reqRefNumber = url.get('reqRefNumber');
  const transactionFee = url.get('transactionFee');

  const handleBackAction = useCallback(() => {
    window?.ReactNativeWebView?.postMessage(
      JSON.stringify({
        navigateBack: true,
      })
    );
  }, []);

  const handleSubmit = () => {
    window?.ReactNativeWebView?.postMessage(
      JSON.stringify({
        submitClick: true,
      })
    );
    setTimeout(() => {
      dispatch(
        onGetMasterpassForMobileRequest({
          TokenType: MasterPassTokenEnum.INITIATE_RECURRING_PAYMENT,
          // ⚠️ Token artık gövdede değil, SDK'yı yetkilendiren JWT olarak taşınıyor.
          jwt: token || '',
          request: {
            phoneNumberForMobile: phoneNumber || '',
            // ⚠️ İşlem yönü artık gövdede değil: yeni SDK'da ekleme ve silme
            // ayrı metotlar olduğu için çağrı yönlendirmesinde kullanılıyor.
            recurringActionType: actionType || 'A',
            // ⚠️ v1 gövdesine giren komisyon alanı. Ekranda gösteriliyordu ama
            // isteğe iletilmiyordu; v1 talimatlarına boş komisyon gidiyordu.
            transactionFee: transactionFee || '',
            // ⚠️ `transactionFee` yeni SDK'nın talimat whitelist'inde yok; bu
            // gövdeyle iletilemiyor.
            data: masterPassRecurringPaymentNormalize(
              cardName || '',
              amount || '',
              endDate || '',
              productId || '',
              reqRefNumber || ''
            ),
          },
        })
      );
    }, 1000);
  };

  useEffect(() => {
    if (!phoneNumber || !token) {
      history.push(routePath.welcome);
    }
    if (language) {
      i18n.changeLanguage(language || '');
      dispatch(setLanguage());
      dispatch(onInıtDataRequest(generateLanguageParam()));
    }
  }, []);

  const getInfoText = useCallback(() => {
    const infotext = web_automatic_instruction_edit_dipnot.split(/(http[s]?:\/\/\S+)/g);
    const infoTextFiltered = infotext.map((item, index) => {
      if (item.startsWith('http://') || item.startsWith('https://')) {
        return (
          <a
            style={{
              color: '#383c52',
            }}
            target="_blank"
            href={item}
            key={index}
          >
            {item}
          </a>
        );
      } else {
        return item;
      }
    });

    return infoTextFiltered;
  }, []);

  return (
    <Box className="paymentOrderWrapper">
      <div id="paymentOrder">
        <Box className="paymentOrder__header">
          <ArrowLeftIcon onClick={handleBackAction} />
          <Typography variant="h3" component="h1" align="center">
            <Box fontWeight={600} marginBottom={gutters.small} marginTop={gutters.regular}>
              {/*
                ⚠️ 'D' DALI ŞART. v2'de talimat silme de bu sayfadan geçiyor
                (Android v2 SDK'sında recurringOrder dışında metot olmadığı için
                silme WebView yoluna alındı). Yön kontrol edilmezse kullanıcı
                "Talimat Ver" başlıklı bir ekranda silme onaylıyor.
              */}
              {actionType === 'D'
                ? web_title_delete_instruction_popup
                : actionType === 'U'
                ? web_automatic_instruction_edit_page_title
                : web_automatic_instruction_apply_instruction}
            </Box>
          </Typography>
        </Box>
        <Box className="paymentOrder__content">
          <Box className="content__row">
            <Box className="content__row__left">
              <p className="content__row__title">{web_automatic_instruction_card_to_be_loaded}</p>
            </Box>
            <Box className="content__row__right">
              <p className="content__row__value">{nickName}</p>
              <span className="content__row__value__desc">{aliasNumber}</span>
            </Box>
          </Box>
          <Box className="content__row">
            <Box className="content__row__left">
              <p className="content__row__title">{web_automatic_instruction_payment_instrument}</p>
            </Box>
            <Box className="content__row__right">
              <p className="content__row__value">{cardName}</p>
              <span className="content__row__value__desc">{cardNo}</span>
            </Box>
          </Box>
          <Box className="content__row">
            <Box className="content__row__left">
              <p className="content__row__title">{web_automatic_instruction_loading_amount}</p>
            </Box>
            <Box className="content__row__right">
              <p className="content__row__value">{credit}</p>
            </Box>
          </Box>
          <Box className="content__row">
            <Box className="content__row__left">
              <p className="content__row__title">{web_automatic_instruction_lower_limit}</p>
            </Box>
            <Box className="content__row__right">
              <p className="content__row__value">{lowerCredit}</p>
            </Box>
          </Box>
          <Box className="content__row">
            <Box className="content__row__left">
              <p className="content__row__title">{web_instruction_transaction_fee}</p>
            </Box>
            <Box className="content__row__right">
              <p className="content__row__value">{transactionFee}</p>
            </Box>
          </Box>
          <Box className="content__row borderBottom">
            <p className="content__row__info__text">{getInfoText()}</p>
          </Box>
        </Box>
      </div>
      <div className="button__container">
        <Button
          className="lighttheme"
          variant="contained"
          color="secondary"
          fullWidth
          onClick={handleBackAction}
          itemID="dialogOtpBtnTest"
        >
          {web_automatic_instruction_edit_giveup_instruction}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          onClick={handleSubmit}
          type="submit"
          itemID="dialogOtpBtnTest"
        >
          {actionType === 'D'
            ? web_btn_delete_instruction
            : web_automatic_instruction_edit_apply_instruction}
        </Button>
      </div>
    </Box>
  );
};

export default MobilePaymentOrder;
